import { JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import AlertBanner from "lib/components/alert/AlertBanner";
import AlertMessage from "lib/components/alert/AlertMessage";
import Prompt from "lib/components/prompt/Prompt";

import {
	ResponseState,
	ResponseStateType,
	mergeState
} from "utils/fetch/FetchResponse";
import useFetch from "utils/fetch/useFetch";
import useGet from "utils/fetch/useGet";
import { DeleteEvent, Event, EventGroup, MoveEvent } from "utils/history/Event";
import History from "utils/history/History";
import { ListInfo } from "utils/list/List";
import TableActionbar from "utils/list/TableActionbar";

import { Right } from "../base/BlueprintService";
import EditActionbar from "../competencelist/EditActionbar";
import DashboardClient from "./DashboardBlueprintClient";
import { DashboardBlueprint } from "./DashboardTypes";
import WidgetTable from "./WidgetTable";
import BlueprintLibraryInfo from "core/blueprint/base/BlueprintLibraryInfo";

export type DashboardBlueprintViewProps = {
	id: number;
};

export default function DashboardBlueprintView(
	props: Readonly<DashboardBlueprintViewProps>
): JSX.Element {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const info = useGet(DashboardClient.getInfo(props.id));
	const contentResponse = useGet(DashboardClient.getContent(props.id));

	const status: ResponseStateType = useMemo(
		() => mergeState(info.response, contentResponse.response),
		[info.response.state, contentResponse.response.state]
	);

	const { call } = useFetch();

	const [content, setContent] = useState<DashboardBlueprint>();
	const [listInfo, setListInfo] = useState<ListInfo<string>>(ListInfo.init());
	const [actionHistory, setActionHistory] = useState(new History(content));
	const [message, setMessage] = useState<AlertMessage>();

	useEffect(() => {
		if (contentResponse.response.state !== ResponseState.SUCCESS) {
			return;
		}

		const data = contentResponse.response.data;
		setContent(data);
		setActionHistory(new History(data));
	}, [contentResponse.response]);

	useEffect(() => {
		if (content === undefined) {
			return;
		}

		const keys = content.widgets.map(widget => widget.uuid);
		setListInfo(prevInfo => prevInfo.withKeys(keys));
	}, [content]);

	const undo = useCallback((): void => {
		console.assert(actionHistory.hasPrevious());
		if (actionHistory.hasPrevious()) {
			setContent(actionHistory.undo());
		}
	}, [actionHistory]);

	const redo = useCallback((): void => {
		console.assert(actionHistory.hasNext());
		if (actionHistory.hasNext()) {
			setContent(actionHistory.redo());
		}
	}, [actionHistory]);

	const save = useCallback((): Promise<void> => {
		console.assert(actionHistory !== undefined);
		setMessage(undefined);

		if (!actionHistory.hasPrevious()) {
			return Promise.resolve();
		}

		const actions = actionHistory.getActions();
		const objects = actions.flatMap(action => action.patches());

		return call(DashboardClient.updateContent(props.id, objects))
			.then(() => {
				setMessage(
					AlertMessage.success(
						t("saveDocumentSuccess", { document: "dashboard" })
					)
				);
				contentResponse.refresh();
			})
			.catch(() =>
				setMessage(
					AlertMessage.error(
						t("saveDocumentFailure", { document: "dashboard" })
					)
				)
			);
	}, [actionHistory, props.id]);

	const close = useCallback((): void => {
		navigate("/blueprints");
	}, [navigate]);

	const handleMoveUp = useCallback((): void => {
		if (content === undefined) {
			return;
		}

		const events: Event[] = [];
		for (const [index, field] of content.widgets.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.push(
					new MoveEvent(
						"/widgets/" + index,
						"/widgets/" + (index - 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [content, listInfo, actionHistory]);

	const handleMoveDown = useCallback((): void => {
		if (content === undefined) {
			return;
		}

		const events: Event[] = [];
		for (const [index, field] of content.widgets.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(
					new MoveEvent(
						"/widgets/" + index,
						"/widgets/" + (index + 1)
					)
				);
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [content, listInfo, actionHistory]);

	const handleDelete = useCallback((): void => {
		if (content === undefined) {
			return;
		}

		const events: Event[] = [];
		for (const [index, field] of content.widgets.entries()) {
			if (listInfo.isVisibleAndSelected(field.uuid)) {
				events.unshift(new DeleteEvent("/widgets/" + index, field));
			}
		}
		const event = new EventGroup(events);
		setContent(actionHistory.apply(event.toAction()));
	}, [content, listInfo, actionHistory]);

	const updateField = useCallback(
		(event: Event): void => {
			console.assert(actionHistory !== undefined);

			event.applyContext("/widgets");
			const action = event.toAction();
			setContent(actionHistory.apply(action));
		},
		[actionHistory]
	);

	const selectFields = useCallback(
		(keys: string[], select: boolean): void => {
			setListInfo(previous => previous.markSelected(keys, select));
		},
		[]
	);

	const handleUpdateBlueprint = useCallback((): void => {
		info.refresh();
		contentResponse.refresh();
	}, [info.refresh, contentResponse.refresh]);

	if (status === ResponseState.LOADING) {
		return (
			<Box display="flex" justifyContent="center">
				<CircularProgress />
			</Box>
		);
	}

	if (status === ResponseState.ERROR) {
		return (
			<AlertBanner
				alert={AlertMessage.error(
					t("loadDocumentError", {
						document: "dashboard"
					})
				)}
			/>
		);
	}

	if (
		info.response.state !== ResponseState.SUCCESS ||
		contentResponse.response.state !== ResponseState.SUCCESS
	) {
		return <></>;
	}

	return (
		<>
			{message && <AlertBanner alert={message} />}
			<Typography id="title" variant="h1">
				{info.response.data.name}
			</Typography>

			{info.response.data.sourceBlueprintId !== null &&
				info.response.data.sourceBlueprintVersion !== null && (
					<BlueprintLibraryInfo
						id={props.id}
						enabled={
							info?.response.data.rights.includes(Right.ADMIN) ??
							false
						}
						blueprintId={info.response.data.sourceBlueprintId}
						blueprintVersion={
							info.response.data.sourceBlueprintVersion
						}
						onUpdate={handleUpdateBlueprint}
					/>
				)}

			<EditActionbar
				undoEnabled={actionHistory.hasPrevious()}
				redoEnabled={actionHistory.hasNext()}
				saveEnabled={actionHistory.hasPrevious()}
				onUndo={undo}
				onRedo={redo}
				onSave={save}
				onClose={close}
			/>

			<TableActionbar
				listInfo={listInfo}
				onMoveUp={handleMoveUp}
				onMoveDown={handleMoveDown}
				onDelete={handleDelete}
			/>
			<WidgetTable
				elements={content?.widgets ?? []}
				listInfo={listInfo}
				editable={
					info?.response.data.rights.includes(Right.WRITE) ?? false
				}
				onSelect={selectFields}
				onChange={updateField}
				onInsert={updateField}
			/>

			<Prompt
				when={actionHistory.hasPrevious()}
				message={t("exitDocumentMessage")}
			/>
		</>
	);
}
